import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import './QAActivityCard.css';

const QAActivityCard = ({ projectId }) => {
  const { user } = useAuth();

  const roleUpper = String(user?.role || '').toUpperCase();
  const isQA = roleUpper === 'QA' || roleUpper === 'TESTER';

  const [period, setPeriod] = useState('30days');
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]); // Empty = All
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [customDates, setCustomDates] = useState({ start: '', end: '' });
  const [error, setError] = useState(null);
  const now = new Date();
  const todayYmd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const isValidYmd = useCallback((value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false;
    const [y, m, d] = value.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return (
      dt.getFullYear() === y &&
      dt.getMonth() === (m - 1) &&
      dt.getDate() === d
    );
  }, []);

  const getCustomDateValidationError = useCallback((dates) => {
    if (period !== 'custom') return '';
    const { start, end } = dates || {};
    if (!start && !end) return '';
    if (!start || !end) return 'Preencha data inicial e data final.';
    if (!isValidYmd(start) || !isValidYmd(end)) return 'Data inválida. Digite uma data válida no formato correto.';
    if (start > todayYmd || end > todayYmd) return 'Não é permitido filtrar com datas futuras.';
    if (start > end) return 'Data inicial não pode ser maior que a data final.';
    return '';
  }, [period, todayYmd, isValidYmd]);

  // Load users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (isQA && user?.id) {
          setUsers([
            {
              id: user.id,
              first_name: user.first_name || '',
              last_name: user.last_name || '',
              email: user.email
            }
          ]);
          setSelectedUserIds([user.id]);
          return;
        }

        const token = localStorage.getItem('token');
        const res = await fetch(`${getApiUrl('users')}?limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setUsers(data.data);
        }
      } catch (e) {
        console.error("Failed to fetch users", e);
      }
    };
    fetchUsers();
  }, [isQA, user?.id, user?.first_name, user?.last_name, user?.email]);

  // Load Stats
  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      
      const apiUrl = getApiUrl('reports/qa-activity');
      // Ensure we can construct a URL object even if apiUrl is relative
      const url = new URL(apiUrl, window.location.origin);
      
      url.searchParams.set('period', period);
      if (projectId) {
        url.searchParams.set('projectId', projectId);
      }
      if (period === 'custom' && customDates.start && customDates.end) {
        const customError = getCustomDateValidationError(customDates);
        if (customError) {
          setError(customError);
          setLoading(false);
          return;
        }
        url.searchParams.set('startDate', customDates.start);
        url.searchParams.set('endDate', customDates.end);
      } else if (period === 'custom') {
        const customError = getCustomDateValidationError(customDates);
        if (customError) {
          setError(customError);
        }
        setLoading(false);
        return;
      }
      if (selectedUserIds.length > 0) {
        url.searchParams.set('userIds', selectedUserIds.join(','));
      }
      
      console.log('Fetching QA stats:', url.toString());
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` }});
      
      if (!res.ok) {
        let apiError = '';
        try {
          const errData = await res.json();
          apiError = errData?.error || '';
        } catch (_) {}
        throw new Error(apiError || `Erro na requisição: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      } else {
        setError(data.error || 'Erro ao carregar dados da API');
      }
    } catch (e) {
      console.error(e);
      setError(e.message || 'Erro de conexão');
    } finally {
      setLoading(false);
    }
  }, [period, selectedUserIds, customDates, projectId, getCustomDateValidationError]);

  useEffect(() => {
    if (period !== 'custom') {
        fetchStats();
    } else if (customDates.start && customDates.end) {
        // Wait for user to maybe finish picking or click ok? 
        // Or just fetch if both are set.
        fetchStats();
    }
  }, [fetchStats, period, customDates]);

  useEffect(() => {
    if (period !== 'custom') return;
    const customError = getCustomDateValidationError(customDates);
    if (customError) {
      setError(customError);
    } else if (error && (
      error === 'Preencha data inicial e data final.' ||
      error === 'Data inválida. Digite uma data válida no formato correto.' ||
      error === 'Não é permitido filtrar com datas futuras.' ||
      error === 'Data inicial não pode ser maior que a data final.'
    )) {
      setError(null);
    }
  }, [period, customDates, getCustomDateValidationError, error]);

  const handleUserToggle = (userId) => {
    if (isQA) return;
    if (userId === 'all') {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(prev => {
        if (prev.includes(userId)) {
          return prev.filter(id => id !== userId);
        } else {
          return [...prev, userId];
        }
      });
    }
  };

  const getPeriodLabel = () => {
    const map = {
      'today': 'Hoje',
      '7days': 'Últimos 7 dias',
      '15days': 'Últimos 15 dias',
      '30days': 'Últimos 30 dias',
      'custom': 'Personalizado'
    };
    return map[period] || period;
  };

  const getUserLabel = () => {
    if (selectedUserIds.length === 0) return 'Todos os QAs';
    if (selectedUserIds.length === 1) {
        const u = users.find(u => u.id === selectedUserIds[0]);
        return u ? `${u.first_name} ${u.last_name}` : 'QA Selecionado';
    }
    return `${selectedUserIds.length} QAs Selecionados`;
  };

  // Render Content Logic
  const renderContent = () => {
    if (loading) return <div className="qa-loading">Carregando dados...</div>;
    if (error) return <div className="qa-error">⚠️ {error}</div>;
    if (!stats || !stats.users_stats) return <div className="qa-empty">Nenhum dado disponível</div>;

    let userStats = stats.users_stats;

    // Filter out users with 0 activity if showing "All" (or if simply requested to reduce clutter)
    // The user requested to only show users with at least 1 "done" (cases or cycles).
    if (selectedUserIds.length === 0) {
        userStats = userStats.filter(u => u.stats.cases_created > 0 || u.stats.active_cycles_count > 0);
    }
    
    if (userStats.length === 0) {
        return <div className="qa-empty">Nenhum usuário com atividade no período.</div>;
    }

    // Scenario 1: Individual View (1 user filtered or only 1 result)
    if (userStats.length === 1) {
      const u = userStats[0];
      return (
        <div className="qa-individual-view">
          <div className="qa-profile">
            <div className="qa-avatar">{u.user.initials}</div>
            <div className="qa-info">
              <div className="qa-name">{u.user.name}</div>
              <div className="qa-period-badge">{getPeriodLabel()}</div>
            </div>
          </div>
          
          <div className="qa-metrics-grid">
            <div className="qa-metric-box">
              <div className="qa-metric-label">Casos Criados</div>
              <div className="qa-metric-value">
                {u.stats.cases_created}
                {u.stats.daily_goal_reached !== null && (
                   <span className="qa-goal-check" title="Meta diária atingida">✅</span>
                )}
              </div>
            </div>

            <div className="qa-metric-box">
              <div className="qa-metric-label">Ciclos Ativos</div>
              <div className="qa-metric-value">{u.stats.active_cycles_count}</div>
              {u.stats.active_cycles_list.length > 0 && (
                !projectId ? (
                  <div className="qa-subtext">
                    {u.stats.active_cycles_list.map((cycle) => (
                      <div key={cycle.id}>
                        • {cycle.name} {cycle.project_name ? `(${cycle.project_name})` : ''}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="qa-subtext">Em andamento: "{u.stats.active_cycles_list[0].name}"</div>
                )
              )}
            </div>
          </div>

          {u.next_task && (
             <div className="qa-next-task">
               <div className="qa-task-label">Próxima tarefa:</div>
               <div className="qa-task-desc">{u.next_task.description}</div>
               {u.next_task.due_date && <div className="qa-task-due">Até {new Date(u.next_task.due_date).toLocaleTimeString()}</div>}
             </div>
          )}
        </div>
      );
    }

    // Scenario 2: Comparison (2 users) or Managerial (>2 users)
    // We can use a unified view with a chart for both.
    
    // Prepare data for chart
    const chartData = userStats.map(u => ({
      name: u.user.name.split(' ')[0], // First name
      casos: u.stats.cases_created,
      ciclos: u.stats.active_cycles_count,
      full_name: u.user.name
    }));

    return (
      <div className="qa-comparison-view">
         <div className="qa-chart-container">
            <h4>Comparativo:</h4>
            <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 40 + 20)}>
              <BarChart layout="vertical" data={chartData} margin={{ top: 5, right: 30, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={true} stroke="#f5f5f5" />
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" width={60} tick={{fontSize: 11}} axisLine={false} tickLine={false} />
                <Tooltip 
                  cursor={{fill: 'rgba(0,0,0,0.05)'}} 
                  contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}
                  itemStyle={{fontSize: '12px'}}
                  labelStyle={{fontWeight: 'bold', marginBottom: '4px'}}
                />
                <Bar dataKey="casos" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Casos Criados" barSize={18}>
                  <LabelList dataKey="casos" position="right" style={{ fontSize: '11px', fill: '#666', fontWeight: 'bold' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
         </div>

         <div className="qa-summary-list">
            <div className="qa-list-header">
              <span>QA</span>
              <span>Casos</span>
              <span>Ciclos</span>
            </div>
            <div className="qa-list-body">
              {userStats.map(u => (
                <div key={u.user.id} className="qa-list-row">
                  <span className="qa-list-name" title={u.user.name}>{u.user.name.split(' ')[0]}</span>
                  <span className="qa-list-val">{u.stats.cases_created}</span>
                  <span className="qa-list-val">{u.stats.active_cycles_count}</span>
                </div>
              ))}
            </div>
         </div>
      </div>
    );
  };

  return (
    <div className="card qa-activity-card">
      <div className="qa-header">
        <div className="qa-title">Atividade dos QAs (Mapeamento de cenários e ciclos)</div>
        <div className="qa-controls">
          {/* Period Dropdown */}
          <div className="qa-dropdown">
            <button className="qa-drop-btn" onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}>
               {getPeriodLabel()} ▼
            </button>
            {showPeriodDropdown && (
              <div className="qa-drop-menu">
                {['today', '7days', '15days', '30days', 'custom'].map(p => (
                   <div key={p} className="qa-drop-item" onClick={() => { setPeriod(p); setShowPeriodDropdown(false); }}>
                     {p === 'today' ? 'Hoje' : p === '7days' ? 'Últimos 7 dias' : p === '15days' ? 'Últimos 15 dias' : p === '30days' ? 'Últimos 30 dias' : 'Personalizado'}
                   </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {period === 'custom' && (
        <div className="qa-custom-dates" style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
            <input 
                type="date" 
                value={customDates.start} 
                max={todayYmd}
                onChange={e => setCustomDates({...customDates, start: e.target.value})} 
                style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '12px' }}
            />
            <span style={{ fontSize: '12px', color: '#666' }}>até</span>
            <input 
                type="date" 
                value={customDates.end} 
                max={todayYmd}
                onChange={e => setCustomDates({...customDates, end: e.target.value})} 
                style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '12px' }}
            />
        </div>
      )}

      {!isQA && (
        <div className="qa-user-selector">
          <div className="qa-dropdown full-width">
            <button className="qa-drop-btn" onClick={() => setShowUserDropdown(!showUserDropdown)}>
              {getUserLabel()} ▼
            </button>
            {showUserDropdown && (
              <div className="qa-drop-menu user-menu">
                <div className="qa-drop-item" onClick={() => handleUserToggle('all')}>
                  <input type="checkbox" checked={selectedUserIds.length === 0} readOnly /> Todos
                </div>
                {users.map(u => (
                  <div key={u.id} className="qa-drop-item" onClick={() => handleUserToggle(u.id)}>
                    <input type="checkbox" checked={selectedUserIds.includes(u.id)} readOnly /> {u.first_name} {u.last_name}
                  </div>
                ))}
                <div className="qa-drop-actions">
                  <button className="qa-action-btn" onClick={() => setShowUserDropdown(false)}>Fechar</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="qa-body">
         {renderContent()}
      </div>
    </div>
  );
};

export default QAActivityCard;



